import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import Iconify from "../../components/iconify/iconify";
import Dialog from "../../components/shared/Dialog";
import List from "../../components/shared/List";
import RHFAutocomplete from "../../components/shared/RHFAutocomplete";
import { color } from "../../utils/constant";
import {
  getDoctorAvailabilityList,
  getDoctorList,
  getAreaList,
  getWeekDayList,
  getPaginationInfo,
} from "../../data/selectors/DoctorAvailability/doctorAvailability.selector";
import useDoctorAvailability from "../../data/talons/DoctorAvailability/useDoctorAvailability";
import DoctorAvailabilityForm from "./DoctorAvailabilityForm";
import { useDoctorAvailabilityForm } from "../../data/talons/DoctorAvailability/useDoctorAvailabilityForm";
import { resetState } from "../../data/reducers/DoctorAvailability/DoctorAvailabilityForm.reducer";

export default function DoctorAvailability() {
  const [showDialog, setShowDialog] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [editId, setEditId] = useState(null);
  const [Open, setOpen] = useState(false);
  const doctorAvailabilityList = useSelector(getDoctorAvailabilityList);
  const doctorList = useSelector(getDoctorList);
  const areaList = useSelector(getAreaList);
  const weekDayList = useSelector(getWeekDayList);
  const paginationInfo = useSelector(getPaginationInfo);
  const { setValue, control } = useForm();
  const dispatch = useDispatch();
  const { getDoctorAvailabilityById, DoctorAvailabilityDelete } =
    useDoctorAvailabilityForm({});

  const {
    gridHeaders,
    loading,
    loader,
    DoctorId,
    AreaId,
    weekDay,
    findDoctorFromLabel,
    findAreaFromLabel,
    findWeekDayFromLabel,
    fetchDoctorAvailability,
    fetchDoctorComboList,
    fetchAreaComboList,
    fetchWeekDayList,
  } = useDoctorAvailability();

  useEffect(() => {
    fetchDoctorComboList();
    fetchAreaComboList();
    fetchWeekDayList();
    fetchDoctorAvailability({
      current_page: 1,
      row_count: rowsPerPage,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage]);

  const onSubmit = useCallback(
    async (e) => {
      e?.preventDefault();
      await fetchDoctorAvailability({
        current_page: 1,
        row_count: rowsPerPage,
      });
    },
    [fetchDoctorAvailability, rowsPerPage]
  );

  return (
    <Container
      sx={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        mt: 1,
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={3} md={2}>
          <RHFAutocomplete
            borderRadius={"12px"}
            name="name"
            label="Doctor Name"
            disabled={editId ? true : false}
            freeSolo
            data={doctorList || []}
            defaultValue={DoctorId}
            onInputChange={(value) => findDoctorFromLabel(value)}
            id={doctorList?.map((option) => option.id)}
            renderOption={(props, option) => (
              <>
                <li {...props} key={option}>
                  {option}
                </li>
              </>
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  color="info"
                  variant="soft"
                />
              ))
            }
            control={control}
            setValue={setValue}
          />
        </Grid>
        <Grid item xs={12} sm={3} md={2}>
          <RHFAutocomplete
            borderRadius={"12px"}
            name="area_name"
            label="Area Name"
            disabled={editId ? true : false}
            freeSolo
            data={areaList || []}
            defaultValue={AreaId}
            onInputChange={(value) => findAreaFromLabel(value)}
            id={areaList?.map((option) => option.id)}
            renderOption={(props, option) => (
              <>
                <li {...props} key={option}>
                  {option}
                </li>
              </>
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  color="info"
                  variant="soft"
                />
              ))
            }
            control={control}
            setValue={setValue}
          />
        </Grid>
        <Grid item xs={12} sm={3} md={2}>
          <RHFAutocomplete
            borderRadius={"12px"}
            name="week_day"
            label="Week Day"
            disabled={editId ? true : false}
            freeSolo
            data={weekDayList || []}
            defaultValue={weekDay}
            onInputChange={(value) => findWeekDayFromLabel(value)}
            id={weekDayList?.map((option) => option.id)}
            renderOption={(props, option) => (
              <>
                <li {...props} key={option}>
                  {option}
                </li>
              </>
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  color="info"
                  variant="soft"
                />
              ))
            }
            control={control}
            setValue={setValue}
          />
        </Grid>
        <Grid
          container
          direction="row"
          justify="flex-right"
          item
          xs={6}
          sm={3}
          md={2}
        >
          <Button
            variant="contained"
            onClick={onSubmit}
            sx={{
              maxHeight: 40,
              backgroundColor: color.button,
              borderRadius: 3,
              "&:hover": {
                backgroundColor: color.button,
              },
            }}
          >
            Get Data
          </Button>
        </Grid>
        <Grid
          item
          xs={6}
          sm={3}
          md={2}
          sx={{ marginLeft: "auto", textAlign: "end" }}
        >
          <Button
            sx={{
              height: 40,
              borderRadius: 3,
              backgroundColor: color.button,
              "&:hover": {
                backgroundColor: color.button,
              },
            }}
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={() => setShowDialog(true)}
          >
            {"New"}
          </Button>
          <Dialog
            handleClose={() => {
              setShowDialog(false);
              setEditId(null);
              dispatch(resetState());
              fetchDoctorAvailability({
                current_page: 1,
                row_count: rowsPerPage,
              });
            }}
            open={showDialog}
            fullWidth={true}
          >
            <DoctorAvailabilityForm
              postSubmit={async () => {
                setShowDialog(false);
                setEditId(null);
                await fetchDoctorAvailability({
                  current_page: 1,
                  row_count: rowsPerPage,
                });
                dispatch(resetState());
              }}
              editId={editId}
            />
          </Dialog>
          <Dialog
            handleClose={() => {
              setOpen(false);
            }}
            open={Open}
          // fullWidth={true}
          >
            <Typography sx={{ fontWeight: "bold" }}>
              Confirm to Delete this Data ?.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "end" }}>
              <Button
                onClick={async () => {
                  const res = await DoctorAvailabilityDelete();
                  if (res === true) {
                    setOpen(false);
                    await getDoctorAvailabilityById(editId);
                    fetchDoctorAvailability({
                      current_page: 1,
                      row_count: rowsPerPage,
                    });
                  }
                }}
                sx={{ background: color.button }}
                variant="contained"
              >
                Yes
              </Button>
              <Button
                onClick={() => setOpen(false)}
                sx={{ background: color.button }}
                variant="contained"
              >
                No
              </Button>
            </Box>
          </Dialog>
        </Grid>
      </Grid>
      <Card
        sx={{
          flexGrow: { md: 1 },
          display: { md: "flex" },
          flexDirection: { md: "column" },
          my: "12px",
          borderRadius: "12px",
          boxShadow:
            "0 0 2px 0 rgba(145, 158, 171, 0.2),0 12px 24px -4px rgba(145, 158, 171, 0.12)",
        }}
      >
        <List
          loading={loading}
          data={doctorAvailabilityList}
          paginationData={paginationInfo}
          columnsData={gridHeaders}
          getRowId={(row) => row.id}
          autoHeight={true}
          pageCount={Number(paginationInfo.total_page)}
          totalRecords={paginationInfo.total_count}
          onPageChange={async (page, rowsPerPage) => {
            setRowsPerPage(rowsPerPage);
            await fetchDoctorAvailability({
              current_page: page,
              row_count: rowsPerPage,
            });
          }}
          enableDelete
          disableDelete
          // onDeleteIconpress={async (row) => {
          //   handleDeleteId(row);
          //   setOpen(true);
          // }}
          enableEdit
          onEditIconPress={async (id) => {
            await getDoctorAvailabilityById(id);
            setEditId(id);
            setShowDialog(true);
          }}
        />
      </Card>
    </Container>
  );
}
